import React, { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  AbsenceApplicationsModal,
  ApplicationsView,
} from '@/components/AbsenceApplication/AbsenceApplicationsModal';
import { AbsenceApplicationContext } from '@/components/AbsenceApplication/context';
import { AbsenceDetailModal } from '@/components/AbsenceApplication/AbsenceDetailModal';
import { AbsenceOverlapModal } from '@/components/AbsenceApplication/AbsenceOverlapModal';
import { AbsenceResultModal } from '@/components/AbsenceApplication/AbsenceResultModal';
import { AbsenceServerErrorModal } from '@/components/AbsenceApplication/AbsenceServerErrorModal';
import { useSubmitNonAttendanceApplication } from '@/components/AbsenceApplication/hooks/useSubmitNonAttendanceApplication';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import type {
  NonAttendanceApplicationDetail,
  SubmitNonAttendanceApplicationRequest,
} from '@/components/AbsenceApplication/types';
import { ErrorMessage, logRequestError } from '@/utils/errors';

enum AbsenceView {
  Closed = 'closed',
  Form = 'form',
  History = 'history',
  Detail = 'detail',
  Overlap = 'overlap',
  Result = 'result',
  ServerError = 'server-error',
}

interface Props {
  children: ReactNode;
}

export const AbsenceApplicationProvider: React.FC<Props> = ({ children }) => {
  const { guardAction } = useProfileRestriction();
  const [view, setView] = useState<AbsenceView>(AbsenceView.Closed);
  const [application, setApplication] =
    useState<NonAttendanceApplicationDetail | null>(null);
  const [lastPayload, setLastPayload] =
    useState<SubmitNonAttendanceApplicationRequest | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    number | null
  >(null);

  const { mutate: retrySubmit, isPending: isRetryingSubmit } =
    useSubmitNonAttendanceApplication();

  const onOpen = useCallback(
    () => guardAction(() => setView(AbsenceView.Form)),
    [guardAction],
  );

  const onClose = useCallback(() => {
    setView(AbsenceView.Closed);
    setApplication(null);
    setLastPayload(null);
    setSelectedApplicationId(null);
  }, []);

  const onOpenList = useCallback(() => setView(AbsenceView.History), []);

  const onOpenDetail = useCallback((applicationId: number) => {
    setSelectedApplicationId(applicationId);
    setView(AbsenceView.Detail);
  }, []);
  const onOverlap = useCallback(() => setView(AbsenceView.Overlap), []);

  const onSuccess = useCallback(
    (
      submitted: NonAttendanceApplicationDetail,
      payload: SubmitNonAttendanceApplicationRequest,
    ) => {
      setApplication(submitted);
      setLastPayload(payload);
      setView(AbsenceView.Result);
    },
    [],
  );

  const onServerError = useCallback(
    (payload: SubmitNonAttendanceApplicationRequest) => {
      setLastPayload(payload);
      setView(AbsenceView.ServerError);
    },
    [],
  );

  const onResultTimeout = useCallback(() => {
    setView(AbsenceView.ServerError);
  }, []);

  const onRetrySubmit = useCallback(() => {
    if (!lastPayload) {
      return;
    }

    retrySubmit(lastPayload, {
      onSuccess: (submitted) => {
        setApplication(submitted);
        setView(AbsenceView.Result);
      },
      onError: (error) => {
        logRequestError(ErrorMessage.SubmitNonAttendanceApplication, error);
      },
    });
  }, [lastPayload, retrySubmit]);

  const isApplicationsOpen =
    view === AbsenceView.Form || view === AbsenceView.History;
  const applicationsInitialView =
    view === AbsenceView.Form ? ApplicationsView.Form : ApplicationsView.List;

  const value = useMemo(
    () => ({ onOpen, onOpenList, onOpenDetail }),
    [onOpen, onOpenList, onOpenDetail],
  );

  return (
    <AbsenceApplicationContext.Provider value={value}>
      {children}
      {isApplicationsOpen && (
        <AbsenceApplicationsModal
          initialView={applicationsInitialView}
          onClose={onClose}
          onOverlap={onOverlap}
          onSuccess={onSuccess}
          onServerError={onServerError}
        />
      )}
      {view === AbsenceView.Detail && selectedApplicationId !== null && (
        <AbsenceDetailModal
          applicationId={selectedApplicationId}
          onClose={onClose}
        />
      )}
      {view === AbsenceView.Overlap && (
        <AbsenceOverlapModal onClose={onClose} />
      )}
      {view === AbsenceView.Result && application && (
        <AbsenceResultModal
          application={application}
          onClose={onClose}
          onTimeout={onResultTimeout}
        />
      )}
      {view === AbsenceView.ServerError && (
        <AbsenceServerErrorModal
          isRetrying={isRetryingSubmit}
          onClose={onClose}
          onRetry={onRetrySubmit}
        />
      )}
    </AbsenceApplicationContext.Provider>
  );
};
